import { useState, useEffect, useRef, useCallback } from 'react';
import { openRemoteChannel } from './remoteTransport';
import {
  MSG, CMD, HEARTBEAT_MS, STALE_AFTER_MS,
  generateCode, isValidCommand, buildSnapshot,
} from './remoteProtocol';

const SESSION_KEY = 'sleekHearRemoteCode';

const readSavedCode = () => {
  try { return sessionStorage.getItem(SESSION_KEY) || null; } catch { return null; }
};
const saveCode = (code) => {
  try {
    if (code) sessionStorage.setItem(SESSION_KEY, code);
    else sessionStorage.removeItem(SESSION_KEY);
  } catch { /* storage unavailable: the code just won't survive a reload */ }
};

/**
 * Console side of the phone remote.
 *
 * start() creates a pairing code and joins its channel (a code from earlier in
 * this browser tab is reused after a reload); end() leaves and forgets it.
 * While paired, the console sends the phone a snapshot whenever something
 * changes (and every few seconds as a heartbeat), and runs the commands the
 * phone sends through the same functions the keyboard shortcuts use.
 *
 * `actions` = { stagePlanItem, sendStagedContentLive, clearLiveContent, nextLyricLine, prevLyricLine }
 */
export default function useRemoteControl({
  sheet, stagedContent, displayContent, isLive, currentLyricLine, slides, actions,
}) {
  const [code, setCode] = useState(readSavedCode);
  const [status, setStatus] = useState('offline'); // our link to Supabase
  const [phoneConnected, setPhoneConnected] = useState(false);
  const remoteRef = useRef(null);
  const lastPhoneSeen = useRef(0);
  const lastSentRef = useRef('');
  // A phone tap on "Live" for a run-sheet row: stage it, then send it once it has actually been staged.
  const pendingLive = useRef(null);

  // Always-current values for the handlers below, which are created once per code.
  const latest = useRef({});
  const snapshot = buildSnapshot({
    plan: sheet.plan, items: sheet.items, currentIndex: sheet.currentIndex,
    stagedContent, displayContent, isLive, currentLine: currentLyricLine, slides,
  });
  latest.current = { sheet, actions, snapshot };

  useEffect(() => {
    if (pendingLive.current === null) return;
    clearTimeout(pendingLive.current);
    pendingLive.current = null;
    latest.current.actions.sendStagedContentLive();
  }, [stagedContent]);

  const sendState = useCallback((force) => {
    const remote = remoteRef.current;
    if (!remote) return;
    const json = JSON.stringify(latest.current.snapshot);
    if (!force && json === lastSentRef.current) return;
    lastSentRef.current = json;
    remote.send({ t: MSG.STATE, state: latest.current.snapshot });
  }, []);

  const runCommand = useCallback((msg) => {
    if (!isValidCommand(msg)) return;
    const { sheet: s, actions: a } = latest.current;
    switch (msg.cmd) {
      case CMD.NEXT: { const item = s.next(); if (item) a.stagePlanItem(item); break; }
      case CMD.PREV: { const item = s.prev(); if (item) a.stagePlanItem(item); break; }
      case CMD.GOTO: {
        const item = s.items[msg.index];
        if (item) {
          s.setCurrentIndex(msg.index);
          if (msg.live === true) {
            clearTimeout(pendingLive.current);
            pendingLive.current = setTimeout(() => { pendingLive.current = null; }, 5000);
          }
          a.stagePlanItem(item);
        }
        break;
      }
      case CMD.SEND: a.sendStagedContentLive(); break;
      case CMD.CLEAR: a.clearLiveContent(); break;
      case CMD.LINE_NEXT: a.nextLyricLine(); break;
      case CMD.LINE_PREV: a.prevLyricLine(); break;
      default: break;
    }
  }, []);

  // Join / leave the channel as the code appears and disappears.
  useEffect(() => {
    if (!code) return undefined;
    lastSentRef.current = '';
    const remote = openRemoteChannel(code, {
      onStatus: setStatus,
      onMessage: (msg) => {
        if (msg.t === MSG.HELLO || msg.t === MSG.PING) {
          lastPhoneSeen.current = Date.now();
          setPhoneConnected(true);
          if (msg.t === MSG.HELLO) sendState(true);
        } else if (msg.t === MSG.CMD) {
          lastPhoneSeen.current = Date.now();
          setPhoneConnected(true);
          runCommand(msg);
        }
      },
    });
    remoteRef.current = remote;
    if (!remote) return undefined;

    const timer = setInterval(() => {
      sendState(true); // heartbeat: lets the phone tell we are still here
      if (Date.now() - lastPhoneSeen.current > STALE_AFTER_MS) setPhoneConnected(false);
    }, HEARTBEAT_MS);

    return () => {
      clearInterval(timer);
      remote.close();
      remoteRef.current = null;
      setStatus('offline');
      setPhoneConnected(false);
      lastPhoneSeen.current = 0;
    };
  }, [code, sendState, runCommand]);

  // Push every change straight away.
  const snapshotJson = JSON.stringify(snapshot);
  useEffect(() => { sendState(false); }, [snapshotJson, sendState, status]);

  const start = useCallback(() => {
    setCode(prev => {
      if (prev) return prev;
      const fresh = generateCode();
      saveCode(fresh);
      return fresh;
    });
  }, []);

  const end = useCallback(() => {
    saveCode(null);
    setCode(null);
  }, []);

  // A new code (the old one stops working immediately).
  const regenerate = useCallback(() => {
    const fresh = generateCode();
    saveCode(fresh);
    setCode(fresh);
  }, []);

  return { code, active: !!code, status, phoneConnected, start, end, regenerate };
}
